import React from "react";
import { useDispatch } from "react-redux";
import { Typography, Button } from "@mui/material";
import type { ApplicationType, Customer } from "@/store/slices/types/applicationFormSlice";
import {
  saveValue,
  sendCustomerDetailsLinkToCustomer,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import { RichTooltip } from "../CommonComponents/RichTooltip";

const buttonStyle = { padding: 1, margin: "10px 0 0 0" };

/** Lists the customer fields still required before an invite/request link can be sent. */
export const ValidateSendLinkToCustomerTooltip = ({ customer }: { customer: Customer }) => {
  return (
    <React.Fragment>
      <Typography fontSize="12px">
        {`Required ${!customer?.firstName ? "First Name, " : ""}
                      ${!customer?.dateOfBirth ? "Date of Birth, " : ""}
                      ${(customer?.emails?.length ?? 0) <= 0 ? "Email, " : ""}
                      ${(customer?.phoneNumbers?.length ?? 0) <= 0 ? "Phone number" : ""}`}
      </Typography>
    </React.Fragment>
  );
};

interface CustomerInviteActionsProps {
  customer: Customer;
  applicationId: string;
  applicationType: ApplicationType;
  isCustomerLogin: boolean;
  /** True when the customer is missing required details, so links cannot be sent yet. */
  isSendLinkBtnDisable: boolean;
  /** True when the broker lacks edit permission on the application. */
  isDisableEdit: boolean;
}

/**
 * Shared by the default and alternative customer detail layouts.
 */
export const CustomerInviteActions = ({
  customer,
  applicationId,
  applicationType,
  isCustomerLogin,
  isSendLinkBtnDisable,
  isDisableEdit,
}: CustomerInviteActionsProps) => {
  const dispatch = useDispatch();

  const handle = {
    sendFormLinkCustomer: () => {
      return dispatch(sendCustomerDetailsLinkToCustomer({ customerId: customer._id, applicationId, applicationType }));
    },
    sendRequestedDocumentLinkToCustomer: () => {
      return dispatch(saveValue({ requestDocumentsModal: { isOpen: true, customerId: customer._id } }));
    },
  };

  return (
    <React.Fragment>
      {!isCustomerLogin && !customer?.isCustomerDetailsSaved && (
        <RichTooltip
          disableHoverListener={false}
          title={
            isSendLinkBtnDisable ? (
              <ValidateSendLinkToCustomerTooltip customer={customer} />
            ) : (
              <React.Fragment>
                <Typography fontSize="12px">
                  {customer?.isDetailsLinkSend
                    ? "Waiting for customer to save details"
                    : `Send a link to the customer to complete customer details.`}
                </Typography>
              </React.Fragment>
            )
          }
        >
          <span>
            <Button
              variant="outlined"
              size="medium"
              style={buttonStyle}
              disabled={isSendLinkBtnDisable || customer?.isDetailsLinkSend}
              onClick={handle.sendFormLinkCustomer}
            >
              {customer?.isDetailsLinkSend ? "In process" : `Invite Customer`}
            </Button>
          </span>
        </RichTooltip>
      )}

      {!isCustomerLogin && (
        <RichTooltip
          disableHoverListener={false}
          title={isSendLinkBtnDisable && <ValidateSendLinkToCustomerTooltip customer={customer} />}
        >
          <span>
            <Button
              variant="outlined"
              size="small"
              disabled={isDisableEdit || isSendLinkBtnDisable}
              style={buttonStyle}
              onClick={handle.sendRequestedDocumentLinkToCustomer}
            >
              {customer?.isDocumentRequestLinkSend ? "Resend document link" : "Request documents"}
            </Button>
          </span>
        </RichTooltip>
      )}
    </React.Fragment>
  );
};
